// src/pages/HomePage.js - React Router対応版
import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useLocalizedNav } from '../i18n/useLocalizedNav';
import { pick, formatNewsDate } from '../i18n/pick';
import { useDocumentMeta } from '../i18n/useDocumentMeta';
import { newsData } from '../data/newsData';
import { heroSliderData } from '../data/heroSliderData';
import HeroSlider from '../components/HeroSlider';
import AnimatedSection from '../components/AnimatedSection';
import { 
  COLORS, 
  FONT_SIZES, 
  FONT_WEIGHTS, 
  SPACING, 
  BORDER_RADIUS, 
  SHADOWS, 
  TRANSITIONS,
  LAYOUT,
  COMPONENTS,
  MOBILE_STYLES 
} from '../styles/constants';
import { applyCardHover, removeCardHover } from '../utils/hoverEffects';


const HomePage = () => {
  const { t } = useTranslation();
  const { lang, navigate } = useLocalizedNav();
  useDocumentMeta('home');

  const styles = {
    newsSection: {
      padding: LAYOUT.section.padding,
      backgroundColor: COLORS.backgroundSecondary,
    },
    container: LAYOUT.container,
    sectionTitle: {
      fontSize: FONT_SIZES.title,
      fontWeight: FONT_WEIGHTS.bold,
      textAlign: 'center',
      marginBottom: SPACING.xxxl,
      color: COLORS.textPrimary,
    },
    newsList: {},
    newsItem: {
      display: 'flex',
      alignItems: 'center',
      padding: `${SPACING.md} ${SPACING.lg}`,
      borderBottom: `1px solid ${COLORS.backgroundTertiary}`,
      gap: SPACING.md,
      cursor: 'pointer',
      transition: TRANSITIONS.base,
    },
    newsDate: {
      color: COLORS.textTertiary,
      fontSize: FONT_SIZES.sm,
      minWidth: '100px',
    },
    newsCategory: {
      backgroundColor: COLORS.categoryBackground,
      color: COLORS.categoryText,
      padding: `4px ${SPACING.sm}`,
      borderRadius: BORDER_RADIUS.sm,
      fontSize: FONT_SIZES.xs,
      fontWeight: FONT_WEIGHTS.bold,
      width: '120px',
    },
    newsTitle: {
      color: COLORS.textPrimary,
      fontSize: FONT_SIZES.base,
      flex: 1,
      textAlign: 'left',
    },
    newsButtonContainer: {
      textAlign: 'center',
      marginTop: SPACING.lg,
    },
    newsButton: {
      ...COMPONENTS.button.base,
      ...COMPONENTS.button.secondary,
    },
    workSection: {
      padding: LAYOUT.section.padding,
      backgroundColor: 'rgb(82, 154, 197)',
    },
    workContent: {
      textAlign: 'center',
    },
    workTitle: {
      fontSize: FONT_SIZES.xxxl,
      fontWeight: FONT_WEIGHTS.bold,
      marginBottom: SPACING.md,
      color: COLORS.textPrimary,
    },
    workDescription: {
      fontSize: FONT_SIZES.base,
      color: COLORS.textTertiary,
      lineHeight: '1.8',
      marginBottom: SPACING.xxxl,
      maxWidth: '800px',
      margin: `0 auto ${SPACING.xxxl}`,
    },
    workGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: SPACING.lg,
      marginTop: SPACING.xl,
    },
    workCard: {
      ...COMPONENTS.card.base,
      textAlign: 'left',
      backgroundColor: COLORS.backgroundSecondary,
    },
    workCardTitle: {
      fontSize: FONT_SIZES.xl,
      fontWeight: FONT_WEIGHTS.bold,
      marginBottom: SPACING.base,
      color: COLORS.primary,
    },
    workCardText: {
      fontSize: FONT_SIZES.base,
      color: COLORS.textTertiary,
      lineHeight: '1.7',
    },
    contactSection: {
      padding: LAYOUT.section.padding,
      backgroundColor: COLORS.backgroundSecondary,
    },
    contactContent: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    contactInfo: {
      textAlign: 'center',
      backgroundColor: COLORS.backgroundPrimary,
      padding: SPACING.lg,
      borderRadius: BORDER_RADIUS.md,
      boxShadow: SHADOWS.base,
    },
    contactInfoItem: {
      marginBottom: SPACING.base,
      fontSize: FONT_SIZES.base,
      color: COLORS.textSecondary,
    },

    // AYUBITO 紹介セクション（和モダン：紺＋赤＋金）
    ayubitoSection: {
      padding: LAYOUT.section.padding,
      backgroundColor: COLORS.backgroundSecondary,
    },
    ayubitoBand: {
      backgroundColor: '#ffffff',
      borderRadius: BORDER_RADIUS.lg,
      boxShadow: SHADOWS.md,
      padding: SPACING.xl,
      display: 'grid',
      gridTemplateColumns: '1.05fr 0.95fr',
      gap: SPACING.xl,
      alignItems: 'center',
      overflow: 'hidden',
    },
    ayubitoBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: SPACING.sm,
      backgroundColor: '#fdecea',
      color: '#d8382b',
      fontWeight: FONT_WEIGHTS.bold,
      fontSize: FONT_SIZES.sm,
      borderRadius: BORDER_RADIUS.full,
      padding: `8px ${SPACING.base}`,
      marginBottom: SPACING.md,
    },
    ayubitoBadgeRel: {
      color: '#3a4d78',
    },
    ayubitoHead: {
      fontSize: FONT_SIZES.xxl,
      fontWeight: FONT_WEIGHTS.bold,
      color: '#14274e',
      lineHeight: '1.4',
      margin: 0,
      textAlign: 'left',
    },
    ayubitoRed: {
      color: '#d8382b',
    },
    ayubitoWord: {
      fontSize: '40px',
      fontWeight: FONT_WEIGHTS.bold,
      letterSpacing: '0.1em',
      color: '#14274e',
      margin: `${SPACING.xs} 0 ${SPACING.base}`,
      textAlign: 'left',
    },
    ayubitoDesc: {
      fontSize: FONT_SIZES.base,
      color: '#3a4d78',
      lineHeight: '1.8',
      marginBottom: SPACING.lg,
      textAlign: 'left',
    },
    ayubitoFeats: {
      display: 'flex',
      gap: SPACING.lg,
      marginBottom: SPACING.lg,
      flexWrap: 'wrap',
    },
    ayubitoFeat: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: SPACING.xs,
      width: '110px',
      textAlign: 'center',
    },
    ayubitoFeatIcon: {
      width: '52px',
      height: '52px',
      borderRadius: BORDER_RADIUS.circle,
      border: '1.5px solid #dbe2f0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#14274e',
    },
    ayubitoFeatTitle: {
      fontSize: FONT_SIZES.sm,
      fontWeight: FONT_WEIGHTS.bold,
      color: '#14274e',
    },
    ayubitoFeatSub: {
      fontSize: FONT_SIZES.xs,
      color: COLORS.textTertiary,
      lineHeight: '1.5',
    },
    ayubitoCta: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: SPACING.sm,
      backgroundColor: '#14274e',
      color: '#ffffff',
      fontWeight: FONT_WEIGHTS.bold,
      fontSize: FONT_SIZES.base,
      padding: `14px ${SPACING.lg}`,
      borderRadius: BORDER_RADIUS.full,
      textDecoration: 'none',
      transition: TRANSITIONS.base,
    },
    ayubitoImageWrap: {
      display: 'flex',
      justifyContent: 'center',
    },
    ayubitoImage: {
      width: '100%',
      maxWidth: '440px',
      height: 'auto',
      display: 'block',
    },

  };

  return (
    <div>
      <style>
        {MOBILE_STYLES}
        {`
          @media (max-width: 768px) {
            .news-item-mobile {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: ${SPACING.sm} !important;
            }
            .news-date-mobile {
              min-width: auto !important;
            }
            .news-category-mobile {
              width: auto !important;
            }
            .news-title-mobile {
              font-size: ${FONT_SIZES.sm} !important;
            }
            .ayubito-band-mobile {
              grid-template-columns: 1fr !important;
              text-align: center !important;
              padding: 28px 20px !important;
            }
            .ayubito-band-mobile h3,
            .ayubito-band-mobile p,
            .ayubito-word-mobile {
              text-align: center !important;
            }
            .ayubito-feats-mobile {
              justify-content: center !important;
            }
            .ayubito-word-mobile {
              font-size: 32px !important;
            }
          }
        `}
      </style>

      {/* ヒーロースライダー */}
      <HeroSlider slides={heroSliderData} />
  
      {/* 新着情報セクション */}
      <section style={styles.newsSection}>
        <div style={styles.container}>
          <AnimatedSection animationType="fadeUp" delay={0}>
            <h2 style={styles.sectionTitle} className="mobile-text-title">
              {t('home.news.title')}
            </h2>
          </AnimatedSection>

          <AnimatedSection animationType="fadeUp" delay={0.2}>
            <div style={styles.newsList}>
              {newsData.slice().reverse().slice(0, 3).map((news, index) => (
                <AnimatedSection
                  key={news.id}
                  animationType="fadeUp"
                  delay={0.1 * index}
                  duration={0.6}
                >
                  <div
                    style={styles.newsItem}
                    className="news-item-mobile mobile-spacing-md"
                    onClick={() => navigate(`/news/${news.id}`)}
                    onMouseEnter={(e) => applyCardHover(e)}
                    onMouseLeave={(e) => removeCardHover(e)}
                  >
                    <div style={styles.newsDate} className="news-date-mobile">
                      {formatNewsDate(news.date, lang)}
                    </div>
                    <div style={styles.newsCategory} className="news-category-mobile">
                      {pick(news.category, lang)}
                    </div>
                    <div style={styles.newsTitle} className="news-title-mobile">
                      {pick(news.title, lang)}
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection animationType="fadeUp" delay={0.4}>
            <div style={styles.newsButtonContainer}>
              <button onClick={() => navigate('/news')} style={styles.newsButton}>
                {t('home.news.more')} →
              </button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* WORKセクション */}
      <section id="work" style={styles.workSection}>
        <div style={styles.container}>
          <AnimatedSection animationType="fadeUp" delay={0}>
            <h2 style={styles.sectionTitle} className="mobile-text-title">WORKS</h2>
          </AnimatedSection>

          <div style={styles.workContent}>
            <div style={styles.workGrid} className="mobile-grid-single mobile-gap-md">
              <AnimatedSection animationType="fadeUp" delay={0.2}>
                <div style={styles.workCard} className="mobile-spacing-md">
                  <h4 style={styles.workCardTitle}>{t('home.work.inbound.title')}</h4>
                  <p style={styles.workCardText}>
                    {t('home.work.inbound.text')}
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection animationType="fadeUp" delay={0.4}>
                <a
                  href="https://web.ayubito.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', display: 'block' }}
                >
                  <div
                    style={styles.workCard}
                    className="mobile-spacing-md"
                    onMouseEnter={(e) => applyCardHover(e)}
                    onMouseLeave={(e) => removeCardHover(e)}
                  >
                    <h4 style={styles.workCardTitle}>{t('home.work.guide.title')}</h4>
                    <p style={styles.workCardText}>
                      {t('home.work.guide.text')}
                    </p>
                    <span style={{ display: 'inline-block', marginTop: SPACING.base, color: '#d8382b', fontWeight: FONT_WEIGHTS.bold, fontSize: FONT_SIZES.sm }}>
                      {t('home.work.guide.more')} →
                    </span>
                  </div>
                </a>
              </AnimatedSection>

              <AnimatedSection animationType="fadeUp" delay={0.6}>
                <div style={styles.workCard} className="mobile-spacing-md">
                  <h4 style={styles.workCardTitle}>{t('home.work.dx.title')}</h4>
                  <p style={styles.workCardText}>
                    {t('home.work.dx.text')}
                  </p>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* AYUBITO 紹介セクション */}
      <section style={styles.ayubitoSection}>
        <div style={styles.container}>
          <AnimatedSection animationType="fadeUp" delay={0}>
            <div style={styles.ayubitoBand} className="ayubito-band-mobile">
              {/* 左：コピー */}
              <div>
                <div style={styles.ayubitoBadge}>
                  {t('ayubito.badge')}
                  <span style={styles.ayubitoBadgeRel}>{t('ayubito.release')}</span>
                </div>
                <h3 style={styles.ayubitoHead}>
                  <Trans
                    i18nKey="ayubito.headline"
                    components={{ hl: <span style={styles.ayubitoRed} />, br: <br /> }}
                  />
                </h3>
                <div style={styles.ayubitoWord} className="ayubito-word-mobile">AYUBITO</div>
                <p style={styles.ayubitoDesc}>
                  {t('ayubito.desc')}
                </p>
                <div style={styles.ayubitoFeats} className="ayubito-feats-mobile">
                  <div style={styles.ayubitoFeat}>
                    <div style={styles.ayubitoFeatIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M3 12h18" />
                        <path d="M12 3c2.5 2.5 2.5 15 0 18" />
                        <path d="M12 3c-2.5 2.5-2.5 15 0 18" />
                      </svg>
                    </div>
                    <div style={styles.ayubitoFeatTitle}>{t('ayubito.feature1.title')}</div>
                    <div style={styles.ayubitoFeatSub}>{t('ayubito.feature1.sub')}</div>
                  </div>
                  <div style={styles.ayubitoFeat}>
                    <div style={styles.ayubitoFeatIcon}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 3h7v7H3V3zm2 2v3h3V5H5z" />
                        <path d="M14 3h7v7h-7V3zm2 2v3h3V5h-3z" />
                        <path d="M3 14h7v7H3v-7zm2 2v3h3v-3H5z" />
                        <path d="M14 14h3v3h-3zM18 18h3v3h-3zM14 18h2v3h-2zM18 14h3v2h-3z" />
                      </svg>
                    </div>
                    <div style={styles.ayubitoFeatTitle}>{t('ayubito.feature2.title')}</div>
                    <div style={styles.ayubitoFeatSub}>{t('ayubito.feature2.sub')}</div>
                  </div>
                  <div style={styles.ayubitoFeat}>
                    <div style={styles.ayubitoFeatIcon}>
                      <svg width="22" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                        <path d="M12 22s7-6.5 7-12A7 7 0 0 0 5 10c0 5.5 7 12 7 12z" />
                        <circle cx="12" cy="10" r="2.5" />
                      </svg>
                    </div>
                    <div style={styles.ayubitoFeatTitle}>{t('ayubito.feature3.title')}</div>
                    <div style={styles.ayubitoFeatSub}>{t('ayubito.feature3.sub')}</div>
                  </div>
                </div>
                <a
                  href="https://web.ayubito.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.ayubitoCta}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#0e1b39'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#14274e'; }}
                >
                  {t('ayubito.cta')} →
                </a>
              </div>

              {/* 右：ビジュアル */}
              <div style={styles.ayubitoImageWrap}>
                <img
                  src={process.env.PUBLIC_URL + '/hero05-1.png'}
                  alt={t('ayubito.imageAlt')}
                  style={styles.ayubitoImage}
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>


      {/* CONTACTセクション */}
      <section id="contact" style={styles.contactSection}>
        <div style={styles.container}>
          <AnimatedSection animationType="fadeUp" delay={0}>
            <h2 style={styles.sectionTitle} className="mobile-text-title">CONTACT</h2>
          </AnimatedSection>

          <AnimatedSection animationType="fadeUp" delay={0.2}>
            <div style={styles.contactContent}>
              <div style={styles.contactInfo} className="mobile-spacing-md">
                <div style={styles.contactInfoItem} className="mobile-text-sm">
                  <strong>{t('home.contact.phoneLabel')}</strong> 06-6357-3668
                </div>
                <div style={styles.contactInfoItem} className="mobile-text-sm">
                  <strong>{t('home.contact.hoursLabel')}</strong> {t('home.contact.hours')}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default HomePage;